import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Receipt, Eye, Send, Loader2, MailCheck, MailX, MailWarning } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

const STATUS = {
  dispatched: { color: "#2FB999", label: "Dispatched", Icon: MailCheck },
  suppressed: { color: "#E5A93E", label: "Suppressed · email switch", Icon: MailWarning },
  failed:     { color: "#E5484D", label: "Delivery failed", Icon: MailX },
};

export function ActivationReceipts({ api, refreshKey }) {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/vcb/receipts?limit=25");
      setRows(r.data?.receipts || []); setCounts(r.data?.counts || {});
    } catch { /* desk still usable without receipts */ }
  }, [api]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const resend = async (r) => {
    setBusy(r.id);
    try {
      const res = await api.post(`/admin/vcb/receipts/${r.id}/resend`);
      toast.success(`Receipt ${res.data.receipt_no} re-dispatched`, { description: r.email });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Resend failed"); }
    finally { setBusy(null); }
  };

  const open = async (r) => {
    try {
      const res = await api.get(`/admin/vcb/receipts/${r.id}/preview`, { responseType: "text" });
      setPreview({ html: res.data, no: r.receipt_no, email: r.email });
    } catch { toast.error("Could not load receipt preview"); }
  };

  return (
    <div className="space-y-3" data-testid="vcb-activation-receipts">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-serif text-xl text-[#F5F5F0] inline-flex items-center gap-2">
          <Receipt size={16} className="text-[#D8B23E]" /> Activation Receipts
        </h3>
        <div className="font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#8A8A82]" data-testid="vcb-receipts-counts">
          {counts.total ?? 0} issued · <span className="text-[#2FB999]">{counts.dispatched ?? 0} dispatched</span>
          {(counts.suppressed ?? 0) > 0 && <> · <span className="text-[#E5A93E]">{counts.suppressed} suppressed</span></>}
          {(counts.failed ?? 0) > 0 && <> · <span className="text-[#E5484D]">{counts.failed} failed</span></>}
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-sm border border-dashed border-white/10 p-6 text-center text-[11px] text-[#8A8A82] italic font-body">
          No receipts yet — every Precision Pulse mints one and emails the member instantly.
        </div>
      ) : (
        <div className="rounded-sm border border-white/10 bg-[#0A0906] overflow-x-auto">
          <table className="w-full text-sm" data-testid="vcb-receipts-table">
            <thead className="border-b border-white/10 bg-[#12100A]">
              <tr className="text-left font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#8A8A82]">
                <th className="py-3 px-3">Receipt №</th>
                <th className="py-3 px-3">Member</th>
                <th className="py-3 px-3">Inducted</th>
                <th className="py-3 px-3">Email</th>
                <th className="py-3 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const s = STATUS[r.email_status] || STATUS.failed;
                return (
                  <motion.tr key={r.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                             transition={{ delay: Math.min(i, 8) * 0.03 }}
                             data-testid={`vcb-receipt-row-${r.id}`}
                             className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="py-3 px-3 font-mono text-[11px] text-[#D8B23E] tracking-wide">{r.receipt_no}</td>
                    <td className="py-3 px-3">
                      <div className="font-body text-sm text-[#F5F5F0]">{r.email}</div>
                      {r.name && <div className="text-[10px] text-[#8A8A82] italic">{r.name}</div>}
                    </td>
                    <td className="py-3 px-3 text-[11px] font-mono text-[#C9C9C2]">
                      {r.inducted_at ? new Date(r.inducted_at).toLocaleString() : "—"}
                    </td>
                    <td className="py-3 px-3">
                      <span className="inline-flex items-center gap-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em]"
                            style={{ color: s.color }} data-testid={`vcb-receipt-status-${r.id}`}>
                        <s.Icon size={11} /> {s.label}
                      </span>
                      {r.resend_count > 0 && (
                        <div className="text-[10px] text-[#8A8A82] italic mt-0.5">re-sent ×{r.resend_count}</div>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button title="Preview receipt" data-testid={`vcb-receipt-preview-${r.id}`}
                                onClick={() => open(r)}
                                className="p-1.5 text-[#8A8A82] hover:text-[#D8B23E] transition-colors">
                          <Eye size={12} />
                        </button>
                        <Button size="sm" variant="outline" data-testid={`vcb-receipt-resend-${r.id}`}
                                onClick={() => resend(r)} disabled={busy === r.id}
                                className="border-[#D8B23E]/40 text-[#D8B23E] hover:bg-[#D8B23E]/10 font-grotesk uppercase tracking-[0.22em] text-[9px] h-7">
                          {busy === r.id ? <Loader2 size={10} className="animate-spin" /> : <><Send size={10} className="mr-1" /> Resend</>}
                        </Button>
                      </div>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent data-testid="vcb-receipt-preview-dialog"
                       className="bg-[#050505] border border-[#D8B23E]/60 text-[#F5F5F0] max-w-3xl p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-white/10">
            <DialogTitle className="font-serif text-xl inline-flex items-center gap-2">
              <Receipt size={14} className="text-[#D8B23E]" /> {preview?.no}
            </DialogTitle>
            <DialogDescription className="text-[11px] font-body text-[#8A8A82]">
              Exactly what {preview?.email} received.
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <iframe title="Activation receipt" srcDoc={preview.html} sandbox=""
                    className="w-full h-[70vh] bg-[#070707] border-0" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
